import { Head, router } from '@inertiajs/react'
import { useState } from 'react'

import AdminLayout from '../../../components/AdminLayout'

type Visitor = {
  id: number
  token: string
  funnel: string
  stage: string
  stageLabel: string
  /** Furthest stage the whole device reached, across every visit. */
  topStage: string
  topStageLabel: string
  deviceType: string | null
  deviceFingerprint: string | null
}

type JourneyEvent = {
  id: number
  name: string
  label: string | null
  path: string | null
  pageName: string | null
  occurredAt: string
  durationMs: number | null
  keyAction: boolean
  meta: Record<string, unknown>
}

type Session = {
  index: number
  startedAt: string
  endedAt: string
  durationSeconds: number
  gapBeforeSeconds: number | null
  pageViews: number
  outcome: string
  won: boolean
  events: JourneyEvent[]
}

type Visit = {
  id: number
  current: boolean
  createdAt: string
  stageLabel: string
}

type ProfileRow = { k: string; v: string }

type Summary = {
  totalVisits: number
  totalSessions: number
  totalEvents: number
  totalDurationSeconds: number
  pageViews: number
  revenue: number
  firstSeenAt: string | null
  lastSeenAt: string | null
}

type Props = {
  visitor: Visitor
  visits: Visit[]
  sessions: Session[]
  profile: { top: ProfileRow[]; more: ProfileRow[] }
  summary: Summary
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function formatSessionDate(iso: string) {
  const date = new Date(iso)
  const day = date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  return `${day} · ${time}`
}

/** 24-hour so the event gutter stays one narrow, aligned column. */
function formatClock(iso: string) {
  return new Date(iso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

/** Compact human duration: 8s, 3m 20s, 1h 04m. */
function formatDuration(seconds: number): string {
  if (seconds <= 0) return '0s'
  if (seconds < 60) return `${seconds}s`

  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  if (minutes < 60) return rest ? `${minutes}m ${rest}s` : `${minutes}m`

  const hours = Math.floor(minutes / 60)
  return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`
}

/** How long the visitor was away before this session started. */
function formatGap(seconds: number): string {
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} later`

  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'} later`

  return `${Math.round(hours / 24)} days later`
}

const EVENT_KIND: Record<string, string> = {
  page_view: 'Viewed',
  cta_click: 'Clicked',
  add_to_cart: 'Add to cart',
  buy_now: 'Buy now',
  remove_from_cart: 'Removed',
  open_cart: 'Opened bag',
  begin_checkout: 'Checkout',
  form_field: 'Filled',
  place_order: 'Placed order',
  purchase: 'Purchased',
}

/** `field` and `value` are rendered by the row itself, not as extra chips. */
const META_HIDDEN = new Set(['field'])

function metaFields(event: JourneyEvent): [string, string][] {
  return Object.entries(event.meta ?? {})
    .filter(([key, value]) => !META_HIDDEN.has(key) && value !== null && value !== '')
    .map(([key, value]) => [key.replace(/_/g, ' '), String(value)])
}

// One device's whole journey: metric strip, collapsible sessions, and a
// profile rail that keeps the technical detail out of the way until asked for.
export default function AdminVisitorsShow({ visitor, visits, sessions, profile, summary }: Props) {
  // Newest session open by default — that's the one you came to read.
  const [openSessions, setOpenSessions] = useState<Record<number, boolean>>(
    sessions.length > 0 ? { [sessions[0].index]: true } : {}
  )
  const [openEvents, setOpenEvents] = useState<Record<number, boolean>>({})
  const [profileOpen, setProfileOpen] = useState(false)
  const [visitsOpen, setVisitsOpen] = useState(false)

  const allOpen = sessions.length > 0 && sessions.every((s) => openSessions[s.index])
  const won = visitor.topStage === 'purchased'

  const toggleAll = () => {
    if (allOpen) return setOpenSessions({})
    setOpenSessions(Object.fromEntries(sessions.map((s) => [s.index, true])))
  }

  const metrics: [string, string | number][] = [
    ['Sessions', summary.totalSessions],
    ['Pages', summary.pageViews],
    ['Events', summary.totalEvents],
    ['Time on site', formatDuration(summary.totalDurationSeconds)],
    ['Visits', summary.totalVisits],
  ]
  if (summary.revenue > 0) {
    metrics.push(['Revenue', `$${summary.revenue.toFixed(2).replace(/\.00$/, '')}`])
  }

  return (
    <AdminLayout>
      <Head title={`Visitor #${visitor.id} — Sick Stuff Admin`} />

      <div className="sss-vz">
        <button type="button" className="sss-admin-back" onClick={() => router.get('/admin/visitors')}>
          ← Back to visitors
        </button>

        <div className="sss-vz-head" style={{ marginTop: 26, marginBottom: 0 }}>
          <div>
            <p className="sss-admin-eyebrow sss-admin-eyebrow--accent">Visitor journey</p>
            <div className="sss-vz-head__badges">
              <h1 className="sss-vz-head__title">Visitor #{visitor.id}</h1>
              <span className={`sss-vz-outcome ${won ? 'is-won' : ''}`}>{visitor.topStageLabel}</span>
            </div>
          </div>
        </div>

        <div className="sss-vz-metrics">
          {metrics.map(([label, value]) => (
            <div className="sss-vz-metric" key={label}>
              <div className="sss-vz-metric__k">{label}</div>
              <div className="sss-vz-metric__v">{value}</div>
            </div>
          ))}
        </div>

        <div className="sss-vz-journey">
          <div>
            <div className="sss-vz-sectionhead">
              {sessions.length > 1 && (
                <button type="button" className="sss-vz-link" onClick={toggleAll}>
                  {allOpen ? 'Collapse all' : 'Expand all'}
                </button>
              )}
            </div>

            {sessions.length === 0 ? (
              <div className="sss-vz-session sss-vz-empty">
                No journey events recorded yet for this visitor.
              </div>
            ) : (
              sessions.map((session, i) => (
                <div key={session.index}>
                  {/* Sessions render newest first, so the gap that separates
                      this one from the card above belongs to that card. */}
                  {i > 0 && sessions[i - 1].gapBeforeSeconds !== null && (
                    <div className="sss-vz-gap">
                      <span>↑ Came back {formatGap(sessions[i - 1].gapBeforeSeconds as number)}</span>
                    </div>
                  )}
                  <SessionBlock
                    session={session}
                    open={!!openSessions[session.index]}
                    onToggle={() =>
                      setOpenSessions((state) => ({
                        ...state,
                        [session.index]: !state[session.index],
                      }))
                    }
                    openEvents={openEvents}
                    onToggleEvent={(id) =>
                      setOpenEvents((state) => ({ ...state, [id]: !state[id] }))
                    }
                  />
                </div>
              ))
            )}
          </div>

          <aside className="sss-vz-profile">
            <div className="sss-vz-profile__head">Profile</div>
            <div className="sss-vz-profile__body">
              {profile.top.map((row) => (
                <div className="sss-vz-prow" key={row.k}>
                  <span className="sss-vz-prow__k">{row.k}</span>
                  <span className="sss-vz-prow__v" title={row.v}>
                    {row.v}
                  </span>
                </div>
              ))}
            </div>

            {profileOpen && (
              <div className="sss-vz-profile__body sss-vz-profile__body--more">
                {profile.more.map((row) => (
                  <div className="sss-vz-prow" key={row.k}>
                    <span className="sss-vz-prow__k">{row.k}</span>
                    <span className="sss-vz-prow__v" title={row.v}>
                      {row.v}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              className="sss-vz-profile__toggle"
              onClick={() => setProfileOpen((open) => !open)}
            >
              {profileOpen ? '− Hide technical details' : '+ Show technical details'}
            </button>

            {visits.length > 1 && (
              <>
                {visitsOpen && (
                  <div className="sss-vz-profile__body sss-vz-profile__body--more">
                    {visits.map((v) => (
                      <div className="sss-vz-prow" key={v.id}>
                        <span className="sss-vz-prow__k">
                          {new Date(v.createdAt).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                        {v.current ? (
                          <span className="sss-vz-prow__v" style={{ color: 'var(--sss-accent)' }}>
                            Current
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="sss-vz-link"
                            onClick={() => router.get(`/admin/visitors/${v.id}`)}
                          >
                            {v.stageLabel}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  className="sss-vz-profile__toggle"
                  onClick={() => setVisitsOpen((open) => !open)}
                >
                  {visitsOpen ? '− Hide' : '+ Show'} {visits.length} visits
                </button>
              </>
            )}
          </aside>
        </div>
      </div>
    </AdminLayout>
  )
}

function SessionBlock({
  session,
  open,
  onToggle,
  openEvents,
  onToggleEvent,
}: {
  session: Session
  open: boolean
  onToggle: () => void
  openEvents: Record<number, boolean>
  onToggleEvent: (id: number) => void
}) {
  const summary = [
    formatDuration(session.durationSeconds),
    `${session.pageViews} page${session.pageViews === 1 ? '' : 's'}`,
    `${session.events.length} event${session.events.length === 1 ? '' : 's'}`,
  ].join(' · ')

  return (
    <section className={`sss-vz-session ${open ? 'is-open' : ''}`}>
      <button type="button" className="sss-vz-session__head" onClick={onToggle}>
        <span className="sss-vz-session__n">{String(session.index).padStart(2, '0')}</span>
        <span>
          <span className="sss-vz-session__date">{formatSessionDate(session.startedAt)}</span>
          <span className="sss-vz-session__sum" style={{ display: 'block' }}>
            {summary}
          </span>
        </span>
        <span className={`sss-vz-outcome ${session.won ? 'is-won' : ''}`}>{session.outcome}</span>
        <span className={`sss-vz-caret ${open ? 'is-open' : ''}`} aria-hidden>
          ⌄
        </span>
      </button>

      {open && (
        <div className="sss-vz-session__body">
          {session.events.map((event) => (
            <EventRow
              key={event.id}
              event={event}
              open={!!openEvents[event.id]}
              onToggle={() => onToggleEvent(event.id)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function EventRow({
  event,
  open,
  onToggle,
}: {
  event: JourneyEvent
  open: boolean
  onToggle: () => void
}) {
  const kind = EVENT_KIND[event.name] ?? event.name.replace(/_/g, ' ')
  const isPage = event.name === 'page_view'
  const heading = event.label ?? event.pageName
  const fields = metaFields(event)
  const hasFields = fields.length > 0

  // Page views show dwell time; everything else shows how much detail it holds.
  const meta = isPage
    ? event.durationMs && event.durationMs > 0
      ? formatDuration(Math.round(event.durationMs / 1000))
      : ''
    : hasFields
      ? `${fields.length} field${fields.length === 1 ? '' : 's'}`
      : ''

  return (
    <div className={`sss-vz-event ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className={`sss-vz-event__main ${hasFields ? 'is-clickable' : ''}`}
        onClick={() => hasFields && onToggle()}
        aria-expanded={hasFields ? open : undefined}
      >
        <span className="sss-vz-event__time">{formatClock(event.occurredAt)}</span>
        <span className={`sss-vz-dot ${event.keyAction ? 'is-hot' : ''}`} />
        <span className={`sss-vz-event__kind ${event.keyAction ? 'is-hot' : ''}`}>{kind}</span>
        <span className="sss-vz-event__label">{heading ?? ''}</span>
        <span className="sss-vz-event__path">{event.path}</span>
        <span className={`sss-vz-event__meta ${hasFields && !isPage ? 'has-fields' : ''}`}>
          {meta}
        </span>
      </button>

      {open && hasFields && (
        <div className="sss-vz-event__fields">
          {fields.map(([key, value]) => (
            <span className="sss-vz-kv" key={key}>
              {key} <b>{value}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
